import {
  GetAccountResponse,
  GetPhoneOptionsResponse,
  PhoneOption,
  PhoneOwnerType,
  PhoneSuffix,
  PhoneType,
  ValidationErrors,
} from "../../../lib/api/models";
import StyledInput from "../../common/inputs/StyledInput";
import Select from "../../common/inputs/Select";
import { PhoneNumberFormInput } from "../PhoneNumber.hooks";
import styles from "./ModifyPhonePrimaryInfo.module.css";

const strings = {
  owner: "Owner*",
  select: "Select",
  firstName: "First Name",
  lastName: "Last Name",
  suffix: "Suffix",
  type: "Type*",
  primaryOrSecondary: "Primary or Secondary",
  none: "None",
  inactiveReason: "Depends on Inactivation Reason",
};

type Props = {
  account: GetAccountResponse;
  options: GetPhoneOptionsResponse;
  formState: PhoneNumberFormInput;
  generalDispatch: (payload: Partial<PhoneNumberFormInput>) => void;
  validationMessages?: ValidationErrors;
  disabled?: boolean;
};

export default function ModifyPhonePrimaryInfo({
  account,
  options,
  formState,
  generalDispatch,
  validationMessages,
  disabled,
}: Props) {
  const suffixOptions: (PhoneOption & { id: PhoneSuffix | "NONE" })[] = [
    ...options.nameSuffixes,
    { id: "NONE", value: strings.none, selectable: true },
  ];

  const disabledIfEditingInactive =
    disabled || (!formState.active && !!formState.phoneId);

  return (
    <div className={styles.container}>
      <Select
        label={strings.owner}
        placeholder={strings.select}
        selectedValue={formState.ownerType}
        onChange={(v: PhoneOwnerType) => {
          v === "CARD_MEMBER"
            ? generalDispatch({
                ownerType: v,
                ownerFirstName: account.firstName,
                ownerLastName: account.lastName,
              })
            : generalDispatch({
                ownerType: v,
                ownerFirstName: "",
                ownerLastName: "",
              });
        }}
        options={options.phoneOwnerTypes
          .filter((o) => o.selectable)
          .map((o) => ({
            key: o.id,
            value: o.id,
            label: o.value,
          }))}
        validationMessages={validationMessages?.ownerType}
        disabled={disabled}
      />
      <StyledInput
        label={strings.firstName}
        value={formState.ownerFirstName}
        onChange={(e) =>
          generalDispatch({ ownerFirstName: e.currentTarget.value })
        }
        validationMessages={validationMessages?.ownerFirstName}
        disabled={disabledIfEditingInactive}
        showPopover={false}
      />
      <StyledInput
        label={strings.lastName}
        value={formState.ownerLastName}
        onChange={(e) =>
          generalDispatch({ ownerLastName: e.currentTarget.value })
        }
        validationMessages={validationMessages?.ownerLastName}
        disabled={disabledIfEditingInactive}
        showPopover={false}
      />
      <Select
        className="select-component"
        label={strings.suffix}
        selectedValue={formState.ownerSuffix ?? "NONE"}
        onChange={(v) =>
          generalDispatch({
            ownerSuffix: v === "NONE" ? null : v,
          })
        }
        options={suffixOptions
          .filter((o) => o.selectable)
          .map((o) => ({
            key: o.id,
            value: o.id,
            label: o.value,
          }))}
        validationMessages={validationMessages?.ownerSuffix}
        disabled={disabledIfEditingInactive}
      />
      <Select
        label={strings.type}
        placeholder={strings.select}
        selectedValue={formState.phoneType}
        onChange={(v: PhoneType) => generalDispatch({ phoneType: v })}
        options={options.phoneTypes
          .filter((o) => o.selectable)
          .map((o) => ({
            key: o.id,
            value: o.id,
            label: o.value,
          }))}
        validationMessages={validationMessages?.phoneType}
        disabled={disabled}
      />
      {!formState.active ? (
        <div>
          <div className={styles.inactiveReasonLabel}>
            {strings.primaryOrSecondary}
          </div>
          <div className={styles.inactiveReasonText}>
            {strings.inactiveReason}
          </div>
        </div>
      ) : (
        <Select
          label={strings.primaryOrSecondary}
          placeholder={strings.select}
          selectedValue={formState.updateFiservCode}
          onChange={(v) => generalDispatch({ updateFiservCode: v })}
          options={options.updateFiservTypes
            .filter((o) => o.selectable)
            .map((o) => ({
              key: o.id,
              value: o.id,
              label: o.value,
            }))}
          validationMessages={validationMessages?.updateFiservType}
          disabled={disabled}
        />
      )}
    </div>
  );
}
